// The desktop shell's rules, pure and import-free so the Worker's suite — the
// only one `npm run check` gates on — can load them (W0).

/** The width at which the desk shell replaces the phone tree. a3-32 (Doug
 *  chose: the desk from 960): a window snapped to half a 1920 monitor, and an
 *  iPad in landscape (1024–1180), get the desk, with the rail folded to
 *  icons and Ask laid over the surface rather than taking a column from it. */
export const DESK_MIN = 960

export const isDesk = (width: number): boolean => width >= DESK_MIN

/** Below this the Ask panel is laid over the working surface instead of
 *  being a column beside it: 380px out of 904 (960 less the rail) would
 *  leave every screen under its own narrowest layout. `desk.css` has the
 *  same number. */
export const ASK_COLUMN_MIN = 1200

export const askOverlays = (width: number): boolean => width < ASK_COLUMN_MIN

/** The rail's order. Ask and Account sit at its foot and are not in this list
 *  as rail entries above the fold, but Account is still a surface screen. */
export const DESK_SCREENS = ['year', 'browse', 'lists', 'health', 'account'] as const
export type DeskScreen = (typeof DESK_SCREENS)[number]

/** What the desk shell does with a route head. `screen: null` means "stay on
 *  whatever is current"; `phone` renders the phone's own component inside the
 *  surface. */
export interface DeskTarget {
  screen: DeskScreen | null
  ask: boolean
  phone: 'city' | 'dev' | null
}

const target = (screen: DeskScreen | null, ask = false, phone: DeskTarget['phone'] = null): DeskTarget =>
  ({ screen, ask, phone })

/** Null-prototype, as the router's alias table is: `constructor` and
 *  `toString` must read as unknown heads, not as entries. */
const TABLE: Record<string, DeskTarget> = Object.assign(Object.create(null), {
  today: target('year'),
  home: target('year'),
  butler: target(null, true),
  feed: target(null, true),
  year: target('year'),
  browse: target('browse'),
  // The phone's place heads (`router.ts`'s PLACE_HEADS). There is no desk
  // Places screen: a place link opens Browse narrowed to that building
  // (`deskRoute.ts`), so `deskCanonical` keeps the id rather than drop it.
  places: target('browse'),
  venues: target('browse'),
  lists: target('lists'),
  health: target('health'),
  account: target('account'),
  city: target(null, false, 'city'),
  dev: target(null, false, 'dev'),
})

/** The raw first segment of a hash, before the router has had a say. The
 *  router drops heads it does not know (`#/health` reads as Today), and the
 *  desk shell has screens the phone does not, so it reads this instead. */
export function deskHead(hash: string): string {
  return hash.replace(/^#\/?/, '').split('?')[0].split('/')[0]
}

/** Heads that only an admin may open (U0.3): Health, and the component
 *  specimen. Everyone else is sent where `DENIED_TO` says. */
export const ADMIN_HEADS: readonly string[] = ['health', 'dev']

/** The rail's own order, Ask and Account at its foot aside. Health is in it
 *  only for an admin; `undefined` (not yet known) is not an admin. */
export const railScreens = (isAdmin: boolean | undefined): readonly DeskScreen[] =>
  isAdmin === true ? ['year', 'browse', 'lists', 'health'] : ['year', 'browse', 'lists']

/** Where a non-admin who types an admin head lands: Health to Browse, the
 *  specimen to the surface a bare hash lands on (the desk's Today). */
const DENIED_TO: Record<string, string> = Object.assign(Object.create(null), {
  health: 'browse',
  dev: 'year',
})

/** What the shell does with a head, once it knows who is asking. `wait` is
 *  "an admin head and the answer is not in" — draw nothing yet, rewrite
 *  nothing: sending Doug to Browse while `app_admins` is still being read
 *  would undo every reload on Health. */
export type DeskAccess =
  | { access: 'ok'; head: string }
  | { access: 'wait' }
  | { access: 'denied'; head: string }

export function deskAccess(head: string, isAdmin: boolean | undefined): DeskAccess {
  if (!ADMIN_HEADS.includes(head) || isAdmin === true) return { access: 'ok', head }
  if (isAdmin === undefined) return { access: 'wait' }
  return { access: 'denied', head: DENIED_TO[head] ?? 'year' }
}

/** An unknown head lands on Year, as the phone's lands on Today. */
export function deskTarget(head: string): DeskTarget {
  return TABLE[head] ?? target('year')
}

/** The hash the shell rewrites a head to once it knows the screen. Most heads
 *  say only where to go (`#/today` → `#/year`), and the rest is dropped as it
 *  always was. A place head carries a building and a query the desk reads, so
 *  it becomes the Browse route the router would make of it —
 *  `#/places/<id>?q=x` → `#/browse/<id>?q=x&mode=places` — which the phone
 *  opens as the same place page. */
export function deskCanonical(hash: string, screen: DeskScreen): string {
  const head = deskHead(hash)
  if (screen !== 'browse' || (head !== 'places' && head !== 'venues')) return `#/${screen}`
  const [path, qs] = hash.replace(/^#\/?/, '').split('?')
  const id = path.split('/')[1]
  const query = new URLSearchParams(qs ?? '')
  query.set('mode', 'places')
  return `#/browse${id ? `/${id}` : ''}?${query}`
}
